import { useEffect, useState } from 'react'
import { Printer, Download, Trash2, AlertTriangle, IdCard } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

/* Kaartjes met inloggegevens om uit te delen bij aankomst.

   De wachtwoorden bestaan alleen in deze pagina-state: ze staan gehasht in de
   database en zijn niet opnieuw op te vragen. Daarom drie uitwegen:
   CSV wegschrijven, kaartjes printen, en een sessie-vangnet tegen een
   per ongeluk verversde pagina.

   Printen gebeurt met CSS in plaats van een aparte route — een nieuwe pagina
   zou de state (en dus de wachtwoorden) kwijt zijn. */

const STORAGE_KEY = 'importCredentialsBackup'

/** Bewaar in sessionStorage: overleeft een refresh, verdwijnt als de tab sluit. */
export function bewaarCredentials(lijst) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), lijst })) } catch {}
}
export function leesCredentials() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const { ts, lijst } = JSON.parse(raw)
    return Array.isArray(lijst) && lijst.length ? { ts, lijst } : null
  } catch { return null }
}
export function wisCredentials() {
  try { sessionStorage.removeItem(STORAGE_KEY) } catch {}
}

export default function CredentialCards({ accounts, siteName = 'LAN Server', siteUrl = 'lanserver.local', onClear }) {
  const [bevestigWis, setBevestigWis] = useState(false)

  // Waarschuw bij het verlaten van de pagina zolang er wachtwoorden openstaan.
  useEffect(() => {
    if (!accounts?.length) return
    const handler = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [accounts])

  if (!accounts?.length) return null

  const downloadCsv = () => {
    // Puntkomma + BOM: zo opent Excel in het Nederlands de kolommen meteen goed.
    const rows = [
      ['Naam', 'Gebruikersnaam', 'Wachtwoord'],
      ...accounts.map(u => [u.display_name ?? '', u.username, u.tijdelijk_wachtwoord]),
    ]
    const csv = '﻿' + rows
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    const datum = new Date().toISOString().slice(0, 10)
    a.download = `lan-inloggegevens-${datum}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const doeWis = () => {
    if (!bevestigWis) { setBevestigWis(true); setTimeout(() => setBevestigWis(false), 4000); return }
    wisCredentials(); onClear?.()
  }

  return (
    <>
      {/* Print-regels: bij afdrukken blijft alleen het kaartjesvel over. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #kaartjes-vel, #kaartjes-vel * { visibility: visible !important; }
          #kaartjes-vel {
            position: absolute; left: 0; top: 0; width: 100%;
            padding: 0; margin: 0; background: #fff;
          }
          .kaartje {
            break-inside: avoid; page-break-inside: avoid;
            border: 1px dashed #999 !important;
            background: #fff !important; color: #000 !important;
          }
          .kaartje .zacht { color: #555 !important; }
          .kaartje .accent { color: #000 !important; }
          @page { margin: 10mm; }
        }
      `}</style>

      <Card className="mt-4" style={{ borderLeft: '2px solid #E4C265' }}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="h-10 w-10 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0">
              <IdCard className="h-5 w-5 text-gold" />
            </div>
            <div className="flex-1 min-w-[16rem]">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Uitdelen bij aankomst
              </div>
              <div className="font-semibold text-sm">Kaartjes met inloggegevens</div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Acht kaartjes per blad, met stippellijn om te knippen. Bewaar eerst
                de CSV — na het sluiten van deze pagina zijn de wachtwoorden weg.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={downloadCsv}>
                <Download className="h-4 w-4 mr-1.5" /> CSV bewaren
              </Button>
              <Button size="sm" variant="outline" onClick={() => window.print()}>
                <Printer className="h-4 w-4 mr-1.5" /> Kaartjes printen
              </Button>
              <Button size="sm" variant="ghost" onClick={doeWis}
                      className={bevestigWis ? 'text-signal' : 'text-muted-foreground'}>
                <Trash2 className="h-4 w-4 mr-1.5" />
                {bevestigWis ? 'Zeker weten?' : 'Van scherm wissen'}
              </Button>
            </div>
          </div>

          <div className="flex items-start gap-2 mt-3 pt-3 border-t border-border/40 text-xs text-muted-foreground">
            <AlertTriangle className="h-3.5 w-3.5 text-gold shrink-0 mt-0.5" />
            <span>
              {accounts.length} {accounts.length === 1 ? 'kaartje' : 'kaartjes'} klaar.
              Iedereen kiest bij de eerste login zelf een nieuw wachtwoord, dus een
              zoekgeraakt kaartje geeft na die eerste keer geen toegang meer.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Het kaartjesvel zelf — op het scherm een voorbeeld, bij printen het enige. */}
      <div id="kaartjes-vel" className="mt-4 grid gap-3 grid-cols-1 sm:grid-cols-2">
        {accounts.map(u => (
          <div key={u.id ?? u.username}
               className="kaartje rounded-[4px] border border-border bg-card p-4"
               style={{ minHeight: '5.5cm' }}>
            <div className="zacht font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
              {siteName}
            </div>

            <div className="text-lg font-bold mt-2 leading-tight break-words">
              {u.display_name || u.username}
            </div>

            <div className="zacht text-[11px] text-muted-foreground mt-3">
              Surf naar <span className="accent font-mono">{siteUrl}</span> en log in met:
            </div>

            <div className="mt-2 space-y-1">
              <div className="flex items-baseline gap-2">
                <span className="zacht font-mono text-[9px] uppercase tracking-widest text-muted-foreground w-20 shrink-0">
                  gebruiker
                </span>
                <span className="font-mono text-sm break-all">{u.username}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="zacht font-mono text-[9px] uppercase tracking-widest text-muted-foreground w-20 shrink-0">
                  wachtwoord
                </span>
                <span className="accent font-mono text-base font-bold text-acid break-all">
                  {u.tijdelijk_wachtwoord}
                </span>
              </div>
            </div>

            <div className="zacht text-[10px] text-muted-foreground mt-3 leading-snug">
              Bij je eerste login kies je zelf een nieuw wachtwoord.
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
